import { useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Spinner,
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// ─── Types ────────────────────────────────────────────────────────────────────

export type SelectOption = { label: string; value: string | number };

export type FieldConfig =
  | {
      name: string;
      label: string;
      type: "text" | "email" | "password" | "number" | "textarea";
      placeholder?: string;
      required?: boolean;
      disabled?: boolean;
      validation?: Record<string, unknown>;
    }
  | {
      name: string;
      label: string;
      type: "select";
      options: SelectOption[];
      placeholder?: string;
      required?: boolean;
      disabled?: boolean;
      validation?: Record<string, unknown>;
    }
  | {
      name: string;
      label: string;
      type: "date" | "datetime";
      placeholder?: string;
      required?: boolean;
      disabled?: boolean;
      minDate?: Date;
      maxDate?: Date;
      validation?: Record<string, unknown>;
    };

export interface EditFormProps {
  title?: string;
  fields: FieldConfig[];
  defaultValues?: Record<string, unknown>;
  onSubmit: (data: Record<string, unknown>) => void | Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  columns?: 1 | 2 | 3;
}

// ─── Normalize ────────────────────────────────────────────────────────────────
// Converts all values to the correct type for each field
// Select → string (HTML select always uses strings)
// null / undefined → "" so inputs are never uncontrolled

const normalizeValues = (
  data: Record<string, unknown>,
  fields: FieldConfig[],
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  fields.forEach((field) => {
    const raw = data[field.name];

    if (field.type === "select") {
      result[field.name] = raw !== null && raw !== undefined ? String(raw) : "";
    } else if (field.type === "date" || field.type === "datetime") {
      result[field.name] = raw ?? null;
    } else {
      result[field.name] = raw !== null && raw !== undefined ? raw : "";
    }
  });

  return result;
};

// ─── Component ────────────────────────────────────────────────────────────────

const EditForm = ({
  title = "Edit",
  fields,
  defaultValues = {},
  onSubmit,
  onCancel,
  submitLabel = "Save Changes",
  cancelLabel = "Cancel",
  loading = false,
  columns = 2,
}: EditFormProps) => {
  const normalized = normalizeValues(defaultValues, fields);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: normalized });

  // Reset whenever parent passes new defaultValues
  // (e.g. fetch completes, or different row selected)
  const prevRef = useRef("");
  useEffect(() => {
    const key = JSON.stringify(defaultValues);

    if (key !== prevRef.current && Object.keys(defaultValues).length > 0) {
      prevRef.current = key;
      reset(normalizeValues(defaultValues, fields));
    }
  }, [defaultValues, fields, reset]);

  const isLoading = loading || isSubmitting;

  const colClass =
    columns === 1
      ? "col-12"
      : columns === 3
        ? "col-md-4 col-sm-6 col-12"
        : "col-md-6 col-12";

  // ── Field renderer — all use Controller so value is always controlled ──
  const renderField = (field: FieldConfig) => {
    const errorMsg = errors[field.name]?.message as string | undefined;
    const rules = {
      required: field.required ? `${field.label} is required` : false,
      ...(field.validation ?? {}),
    };

    // ── Text / Email / Password / Number / Textarea ──
    if (
      field.type === "text" ||
      field.type === "email" ||
      field.type === "password" ||
      field.type === "number" ||
      field.type === "textarea"
    ) {
      return (
        <FormGroup>
          <Label className="form-label fw-semibold">
            {field.label}
            {field.required && <span className="text-danger ms-1">*</span>}
          </Label>
          <Controller
            name={field.name}
            control={control}
            rules={rules}
            render={({ field: ctrl }) => (
              <Input
                type={field.type === "textarea" ? "textarea" : field.type}
                rows={field.type === "textarea" ? 3 : undefined}
                placeholder={field.placeholder ?? `Enter ${field.label}`}
                disabled={field.disabled || isLoading}
                invalid={!!errorMsg}
                value={(ctrl.value as string) ?? ""}
                onChange={ctrl.onChange}
                onBlur={ctrl.onBlur}
                name={ctrl.name}
              />
            )}
          />
          {errorMsg && <FormFeedback>{errorMsg}</FormFeedback>}
        </FormGroup>
      );
    }

    // ── Select ──
    if (field.type === "select") {
      return (
        <FormGroup>
          <Label className="form-label fw-semibold">
            {field.label}
            {field.required && <span className="text-danger ms-1">*</span>}
          </Label>
          <Controller
            name={field.name}
            control={control}
            rules={rules}
            render={({ field: ctrl }) => (
              <Input
                type="select"
                disabled={field.disabled || isLoading}
                invalid={!!errorMsg}
                value={(ctrl.value as string) ?? ""}
                onChange={ctrl.onChange}
                onBlur={ctrl.onBlur}
                name={ctrl.name}
              >
                <option value="">
                  {field.placeholder ?? `Select ${field.label}`}
                </option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </option>
                ))}
              </Input>
            )}
          />
          {errorMsg && <FormFeedback>{errorMsg}</FormFeedback>}
        </FormGroup>
      );
    }

    // ── Date / Datetime ──
    if (field.type === "date" || field.type === "datetime") {
      return (
        <FormGroup>
          <Label className="form-label fw-semibold">
            {field.label}
            {field.required && <span className="text-danger ms-1">*</span>}
          </Label>
          <Controller
            name={field.name}
            control={control}
            rules={rules}
            render={({ field: ctrl }) => (
              <>
                <DatePicker
                  className={`form-control${errorMsg ? " is-invalid" : ""}`}
                  selected={ctrl.value ? new Date(ctrl.value as string) : null}
                  onChange={(date) => ctrl.onChange(date)}
                  dateFormat={
                    field.type === "datetime"
                      ? "dd/MM/yyyy HH:mm:ss"
                      : "dd/MM/yyyy"
                  }
                  showTimeSelect={field.type === "datetime"}
                  timeFormat="HH:mm:ss"
                  placeholderText={field.placeholder ?? `Select ${field.label}`}
                  disabled={field.disabled || isLoading}
                  minDate={field.minDate}
                  maxDate={field.maxDate}
                  wrapperClassName="w-100"
                  autoComplete="off"
                />
                {errorMsg && (
                  <div className="invalid-feedback d-block">{errorMsg}</div>
                )}
              </>
            )}
          />
        </FormGroup>
      );
    }

    return null;
  };

  return (
    <Card>
      {title && (
        <CardHeader className="pb-2">
          <h5 className="mb-0">{title}</h5>
        </CardHeader>
      )}
      <CardBody>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="row">
            {fields.map((field) => (
              <div key={field.name} className={colClass}>
                {renderField(field)}
              </div>
            ))}
          </div>

          <div className="d-flex gap-2 justify-content-end mt-3">
            {onCancel && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onCancel}
                disabled={isLoading}
              >
                {cancelLabel}
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary main-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                submitLabel
              )}
            </button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default EditForm;
